import React, { useEffect, useState } from 'react';
import { useAuth } from '@/components/auth';
import { participantService } from '@/client/services/participants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { CheckCircle, XCircle, Clock, Gift, ExternalLink, AlertTriangle, Music, Mail, Edit } from 'lucide-react';
import { toast } from 'sonner';

interface ParticipantRewardStatus {
  email: string | null;
  rewardCode: string | null;
  annotationCount: number;
  rewardEligible: boolean;
  rewardClaimed: boolean;
  rewardClaimedAt: string | null;
  avgListeningPercentage: number;
  meetsListeningRequirement: boolean;
  top2ListeningPercentages?: number[];
}

export function MyRewardsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rewardStatus, setRewardStatus] = useState<ParticipantRewardStatus | null>(null);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  const fetchRewardStatus = async () => {
    try {
      // Get actual reward status with real listening percentage
      const status = await participantService.getRewardStatus();
      
      setRewardStatus({
        email: status.email,
        rewardCode: status.rewardCode,
        annotationCount: status.annotationCount,
        rewardEligible: status.isFullyEligible, // Use the calculated full eligibility
        rewardClaimed: status.rewardClaimed,
        rewardClaimedAt: status.rewardClaimedAt,
        avgListeningPercentage: status.avgListeningPercentage,
        meetsListeningRequirement: status.meetsListeningRequirement,
        top2ListeningPercentages: status.top2ListeningPercentages
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reward status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewardStatus();
  }, []);

  const handleUpdateEmail = async () => {
    if (!newEmail) {
      toast.error('Please enter an email address');
      return;
    }

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsUpdatingEmail(true);
    try {
      await participantService.updateEmail(newEmail);
      toast.success('Email updated successfully!');
      setIsEmailDialogOpen(false);
      setNewEmail('');
      // Refresh reward status
      await fetchRewardStatus();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update email');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!rewardStatus) {
    return null;
  }

  const discordLink = 'https://discord.gg/fvfjuRc2Tk';
  const hasEnoughAnnotations = rewardStatus.annotationCount >= 2;
  const isFullyEligible = hasEnoughAnnotations && rewardStatus.meetsListeningRequirement && rewardStatus.email;
  const annotationsRemaining = Math.max(0, 2 - rewardStatus.annotationCount);
  
  // Determine what's missing for eligibility
  const missingRequirements = [];
  if (!hasEnoughAnnotations) missingRequirements.push(`${annotationsRemaining} more annotation${annotationsRemaining !== 1 ? 's' : ''}`);
  if (!rewardStatus.meetsListeningRequirement) missingRequirements.push('higher listening percentage (need 75%+)');
  if (!rewardStatus.email) missingRequirements.push('email address');

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">My Reward Status</h1>
        <p className="text-muted-foreground">
          Track your progress toward earning Tensorpool credits
        </p>
      </div>

      {/* Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5" />
            Reward Eligibility Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Eligibility Summary */}
          <div className={`p-4 rounded-lg border-2 ${
            isFullyEligible 
              ? 'bg-green-50 dark:bg-green-950/50 border-green-200 dark:border-green-800' 
              : 'bg-blue-50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-800'
          }`}>
            <div className="flex items-center gap-3 mb-2">
              {isFullyEligible ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <Clock className="h-6 w-6 text-blue-600" />
              )}
              <h3 className="text-lg font-semibold">
                {isFullyEligible 
                  ? rewardStatus.rewardClaimed 
                    ? 'Reward Claimed!' 
                    : 'You\'re Eligible!' 
                  : 'Keep Going!'}
              </h3>
            </div>
            <p className="text-sm">
              {isFullyEligible 
                ? rewardStatus.rewardClaimed
                  ? 'You have already claimed your $20 Tensorpool reward.'
                  : 'You\'ve met all requirements! Claim your $20 Tensorpool reward now.'
                : missingRequirements.length > 0
                  ? `To become eligible, you need: ${missingRequirements.join(', ')}.`
                  : 'Complete the requirements to earn your reward.'}
            </p>
          </div>

          {/* Progress Checklist */}
          <div className="space-y-3">
            <h4 className="font-semibold">Requirements:</h4>
            
            {/* Annotation Count */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              {rewardStatus.annotationCount >= 2 ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="font-medium">Complete 2 Annotations</p>
                <p className="text-sm text-muted-foreground">
                  {rewardStatus.annotationCount} of 2 completed
                </p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold">{rewardStatus.annotationCount}</span>
                <span className="text-muted-foreground">/2</span>
              </div>
            </div>

            {/* Listening Requirement */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              {rewardStatus.meetsListeningRequirement ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="font-medium">Listen to 75% of Top 2 Songs</p>
                <p className="text-sm text-muted-foreground">
                  Best 2 annotations average: {rewardStatus.avgListeningPercentage.toFixed(1)}%
                  {rewardStatus.top2ListeningPercentages && rewardStatus.top2ListeningPercentages.length > 0 && (
                    <span className="block text-xs mt-1">
                      ({rewardStatus.top2ListeningPercentages.map(p => `${p.toFixed(0)}%`).join(', ')})
                    </span>
                  )}
                </p>
              </div>
              <div className="text-right">
                <span className={`text-2xl font-bold ${rewardStatus.meetsListeningRequirement ? 'text-green-600' : ''}`}>
                  {rewardStatus.avgListeningPercentage.toFixed(0)}%
                </span>
              </div>
            </div>

            {/* Email Provided */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              {rewardStatus.email ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="font-medium">Provide Email Address</p>
                <p className="text-sm text-muted-foreground">
                  {rewardStatus.email ? `Email: ${rewardStatus.email}` : 'No email provided'}
                </p>
              </div>
              <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    {rewardStatus.email ? (
                      <>
                        <Edit className="h-3 w-3" />
                        Change
                      </>
                    ) : (
                      <>
                        <Mail className="h-3 w-3" />
                        Add Email
                      </>
                    )}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {rewardStatus.email ? 'Update Email Address' : 'Add Email Address'}
                    </DialogTitle>
                    <DialogDescription>
                      {rewardStatus.email 
                        ? 'Update your email address for reward verification and claiming.'
                        : 'Add your email address to become eligible for rewards. This email will be used to verify and claim your Tensorpool credits.'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {rewardStatus.email && (
                      <div className="text-sm">
                        <Label>Current Email</Label>
                        <p className="text-muted-foreground mt-1">{rewardStatus.email}</p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        {rewardStatus.email ? 'New Email Address' : 'Email Address'}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdateEmail();
                          }
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        This email will be used to verify your annotations and claim your reward on Discord.
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEmailDialogOpen(false);
                        setNewEmail('');
                      }}
                      disabled={isUpdatingEmail}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleUpdateEmail} disabled={isUpdatingEmail}>
                      {isUpdatingEmail ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Updating...
                        </>
                      ) : (
                        'Update Email'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Reward Details */}
          <div className="p-4 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h4 className="font-semibold mb-2">Reward Details:</h4>
            <ul className="space-y-1 text-sm">
              <li>💰 <strong>$20 in Tensorpool Credits</strong></li>
              <li>⚡ Equivalent to <strong>20 hours of H100 GPU time</strong></li>
              <li>🎵 $10 per annotation (max 2 annotations)</li>
              <li>🎧 Your <strong>best 2 annotations</strong> must average 75%+ listening</li>
              <li>✨ Extra annotations don't hurt - we only count your top 2!</li>
            </ul>
          </div>

          {/* Action Button */}
          {isFullyEligible && !rewardStatus.rewardClaimed && (
            <div className="space-y-3">
              <h4 className="font-semibold">Next Steps:</h4>
              <div className="p-4 bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-800 rounded-lg mb-4">
                <p className="font-semibold mb-2">Your Reward Code:</p>
                <div className="flex items-center gap-2">
                  <code className="text-2xl font-mono font-bold bg-white dark:bg-gray-900 px-4 py-2 rounded border-2 border-green-600">
                    {rewardStatus.rewardCode}
                  </code>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Use this code to claim your reward (keeps your email private)
                </p>
              </div>
              <ol className="list-decimal list-inside space-y-2 text-sm mb-4">
                <li>Join the Tensorpool Discord server</li>
                <li>Navigate to the LAION credit channel</li>
                <li>Post your reward code: <code className="bg-muted px-2 py-1 rounded font-mono font-bold">{rewardStatus.rewardCode}</code></li>
                <li>Provide your Tensorpool email for credit transfer</li>
                <li>Wait for verification and credit transfer</li>
              </ol>
              <Button
                className="w-full gap-2"
                onClick={() => window.open(discordLink, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
                Join Tensorpool Discord to Claim
              </Button>
            </div>
          )}

          {/* Already Claimed */}
          {rewardStatus.rewardClaimed && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Your reward was claimed{rewardStatus.rewardClaimedAt && ` on ${new Date(rewardStatus.rewardClaimedAt).toLocaleDateString()}`}. 
                Check your Tensorpool account for the credits.
              </AlertDescription>
            </Alert>
          )}

          {/* Missing Email Warning */}
          {!rewardStatus.email && rewardStatus.annotationCount > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>You need to provide an email address to claim your reward.</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEmailDialogOpen(true)}
                  className="ml-4"
                >
                  Add Email Now
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Need More Work */}
          {!isFullyEligible && !rewardStatus.rewardClaimed && missingRequirements.length > 0 && (
            <Alert>
              <Music className="h-4 w-4" />
              <AlertDescription>
                <strong>To become eligible:</strong> {missingRequirements.join(', ')}.
                {!rewardStatus.meetsListeningRequirement && rewardStatus.annotationCount > 0 && (
                  <span className="block mt-2">
                    💡 Tip: We count your best 2 annotations. Make sure to listen to at least 75% of each song 
                    before submitting. Your top 2 average is {rewardStatus.avgListeningPercentage.toFixed(1)}%.
                    {rewardStatus.top2ListeningPercentages && rewardStatus.top2ListeningPercentages.length > 0 && (
                      <span className="block mt-1">
                        Your best annotations: {rewardStatus.top2ListeningPercentages.map(p => `${p.toFixed(0)}%`).join(', ')}
                      </span>
                    )}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Need Help?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p><strong>Q: How do I complete more annotations?</strong></p>
          <p className="text-muted-foreground">Return to the home page and start a new annotation session.</p>
          
          <p className="mt-3"><strong>Q: How is my listening percentage calculated?</strong></p>
          <p className="text-muted-foreground">We take your top 2 annotations by listening percentage and average them. This means you can have some annotations with lower listening without affecting your eligibility!</p>
          
          <p className="mt-3"><strong>Q: Why isn't my listening percentage higher?</strong></p>
          <p className="text-muted-foreground">Make sure to listen to at least 75% of each song before submitting. Your best 2 annotations need to average 75%+ for eligibility.</p>
          
          <p className="mt-3"><strong>Q: I forgot to add my email, what should I do?</strong></p>
          <p className="text-muted-foreground">Contact the platform administrators to add your email retroactively.</p>
        </CardContent>
      </Card>
    </div>
  );
}
